import * as React from "react";
import {
  AutoComplete,
  Button,
  Input,
  Select,
  Tooltip,
  theme,
  Space,
} from "antd";
import {
  DeleteOutlined,
  EyeFilled,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { FormulaInputClause, UseQuestionInputClauses } from "./definitions";
import { Clause } from "./Clause";
import { useMenuEscape } from "./ClauseMenu";
import { ColumnFormula, Formula, SearchState } from "src/search/types";
import {
  figureFormulaParams,
  formulaFunctions,
  formulaToString,
  isValid,
  matchesTypes,
  relevantColumnsForFormula,
} from "@vzbl/integrations";
import { Flex } from "src/components/Flex";
import { mono } from "src/fonts";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { asKey } from "src/utils/fields";
import { usePopupContext } from "src/hooks/usePopup";
import { FormulaIcon } from "src/components/FormulaIcon";
import { FieldTooltip } from "./ClauseTooltips";
import { CustomTooltip } from "src/components/CustomTooltip";
import styles from "./question-input.module.css";
type ColInfo = ReturnType<typeof relevantColumnsForFormula>[number];

// Add template definitions
const FORMULA_TEMPLATES = [
  {
    name: "Addition",
    description: "Add two values together",
    formula: ["+", "", ""] as Formula,
  },
  {
    name: "Percentage",
    description: "Calculate percentage (value / total * 100)",
    formula: ["*", ["/", "", ""], "100"] as Formula,
  },
  {
    name: "Ratio",
    description: "Calculate ratio between two values",
    formula: ["/", "", ""] as Formula,
  },
  {
    name: "Growth Rate",
    description: "Calculate growth rate ((new - old) / old * 100)",
    formula: ["*", ["/", ["-", "", ""], ""], "100"] as Formula,
  },
  {
    name: "Average with Filter",
    description: "Average of values with a condition",
    formula: ["avg", ""] as Formula,
  },
  {
    name: "Complex Metric",
    description: "Combination of multiple metrics",
    formula: ["+", ["*", "", "2"], ["avg", ""]] as Formula,
  },
] as const;

// Add helper function to format formula string
function formatFormulaString(formula: string): string {
  let depth = 0;
  let result = "";
  let inFunction = false;

  for (const char of formula) {
    if (char === "(") {
      depth++;
      result += char + "".repeat(depth);
      inFunction = true;
    } else if (char === ")") {
      depth--;
      result += "".repeat(depth) + char;
      inFunction = false;
    } else if (["+", "-", "*", "/"].includes(char) && !inFunction) {
      result += "".repeat(depth) + char + "".repeat(depth);
    } else {
      result += char;
    }
  }

  return result;
}

interface FormulaMenuProps {
  data: Partial<ColumnFormula>;
  setData: (val: Partial<ColumnFormula>) => void;
  columns: ColInfo[];
  onCancel?: () => void;
  onApply?: () => void;
}

function FormulaMenu({
  data,
  setData,
  columns,
  onCancel,
  onApply,
}: FormulaMenuProps) {
  useMenuEscape();
  const [localData, setLocalData] =
    React.useState<Partial<ColumnFormula>>(data);
  const { resize } = usePopupContext();
  const { token } = theme.useToken();

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  React.useEffect(() => resize, [localData, resize]);

  const asString = formulaToString(localData.formula, columns);
  const formattedString = asString ? formatFormulaString(asString) : "";
  const isValidFormula = localData.name && localData.formula;

  const handleClear = () => {
    const emptyState: Partial<ColumnFormula> = {
      name: undefined,
      formula: undefined,
    };
    setLocalData(emptyState);
    setData(emptyState);
  };

  const handleFormulaUpdate = (
    _kind: "formula" | "column",
    value: Formula | string
  ) => {
    const newState = {
      ...localData,
      formula: value as Formula,
    };
    setLocalData(newState);
    setData(newState);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newState = { ...localData, name: e.target.value };
    setLocalData(newState);
    setData(newState);
  };

  const handleTemplateChange = (value: string | undefined) => {
    const template = FORMULA_TEMPLATES.find((t) => t.name === value);
    if (template) {
      const newState = {
        ...localData,
        formula: template.formula,
      };
      setLocalData(newState);
      setData(newState);
    }
  };

  const handleRemoveFilter = () => {
    const newState = {
      ...localData,
      filter: undefined,
    };
    setLocalData(newState);
    setData(newState);
  };

  return (
    <div
      className={styles.clausePanel}
      style={{
        maxHeight: "60vh",
        width: "500px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          position: "relative",
          zIndex: 1,
          minWidth: 0,
        }}
      >
        <FieldTooltip type="formula">
          <span style={{ fontSize: 16, fontWeight: 500 }}>Formula</span>
        </FieldTooltip>
        <div style={{ display: "flex", gap: 8 }}></div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          zIndex: 1,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Input
          placeholder="Name your formula..."
          value={localData.name || ""}
          onChange={handleNameChange}
          style={{ marginBottom: 16 }}
        />
        {formattedString && (
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 8,
                color: token.colorTextSecondary,
              }}
            >
              Formula Preview
            </div>
            <div
              style={{
                fontFamily: mono.style.fontFamily,
                fontSize: 13,
                padding: 12,
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: 6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.4,
                maxHeight: "150px",
                overflowY: "auto",
              }}
            >
              <code>{formattedString}</code>
            </div>
          </div>
        )}

        <FormulaInput
          root={true}
          formula={localData.formula}
          columns={columns}
          possibleTypes={undefined}
          updateFormula={handleFormulaUpdate}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 4,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          paddingTop: 16,
          marginTop: "auto",
        }}
      >
        <div
          style={{
            fontSize: "0.85em",
            color: token.colorTextDescription,
          }}
        ></div>
        <Space>
          <button
            onClick={onCancel}
            style={{
              background: "none",
              border: "none",
              padding: "5px 12px",
              borderRadius: 6,
              cursor: "pointer",
              color: token.colorTextSecondary,
              fontSize: 14,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = token.colorBgTextHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Cancel
          </button>

          <button
            style={{
              fontSize: 14,
              backgroundColor: token.colorPrimary,
              color: "white",
              border: "none",
              padding: "5px 16px",
              borderRadius: 6,
              cursor: isValidFormula ? "pointer" : "not-allowed",
              opacity: isValidFormula ? 1 : 0.5,
              transition: "all 0.2s",
              boxShadow: "0 2px 0 rgba(5, 145, 255, 0.1)",
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = token.colorPrimaryHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = token.colorPrimary;
              }
            }}
            disabled={!isValidFormula}
            onClick={onApply}
          >
            Apply
          </button>
        </Space>
      </div>
    </div>
  );
}

export function FormulaClause({
  clause,
  value,
  removeClause,
  updateClause,
  sameAsLast,
}: {
  clause: FormulaInputClause;
  value: SearchState;
  sameAsLast: boolean;
} & Pick<UseQuestionInputClauses, "removeClause" | "updateClause">) {
  const opened = React.useRef(false);
  const { close } = usePopupContext();
  const menuDataRef = React.useRef<Partial<ColumnFormula>>();

  const clauseData = React.useMemo(
    () => ({ name: clause.value?.name, formula: clause.value?.formula }),
    [clause.value]
  );

  const integrationConfig = useIntegrationConfig();
  const columns = React.useMemo(
    () => relevantColumnsForFormula(value, integrationConfig.data as any),
    [value, integrationConfig]
  );

  const handleFormulaDataUpdate = (v: Partial<ColumnFormula>) => {
    menuDataRef.current = v;
  };

  const handleApply = () => {
    const currentData = menuDataRef.current;
    if (currentData?.name && isValid(currentData.formula, columns).result) {
      const updatedClause = {
        ...clause,
        value: {
          ...(clause.value as ColumnFormula),
          name: currentData.name,
          formula: currentData.formula as ColumnFormula["formula"],
        },
      };
      updateClause(updatedClause);
      opened.current = false;
      close();
    }
  };

  const handleCancel = () => {
    menuDataRef.current = undefined;
    opened.current = false;
    close();
  };

  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => {
        opened.current = false;
        menuDataRef.current = undefined;
      }}
      renderMenu={() => {
        opened.current = true;
        return (
          <FormulaMenu
            data={clauseData}
            setData={handleFormulaDataUpdate}
            columns={columns}
            onCancel={handleCancel}
            onApply={handleApply}
          />
        );
      }}
      clause={clause}
      title={sameAsLast ? "" : "Formulas"}
      icon={
        sameAsLast ? undefined : (
          <FormulaIcon size={10} style={{ opacity: 0.7 }} />
        )
      }
    >
      <Flex inline start gap={4}>
        {(opened.current || clauseData) && (
          <span>
            {clauseData.name ? (
              <>
                <span>{clauseData.name}()</span>
              </>
            ) : (
              <span>f()</span>
            )}
          </span>
        )}
      </Flex>
    </Clause>
  );
}

function ColumnInput({
  column,
  columns,
  update,
  possibleTypes,
  placeholder,
}: {
  column: string;
  columns: ColInfo[];
  update: (kind: "column" | "function", value: string) => void;
  placeholder: string;
  possibleTypes: undefined | string[];
}) {
  const [colOrFunc, setColOrfunc] = React.useState(
    column.length > 0 ? column : undefined
  );
  const { token } = theme.useToken();
  const [searchText, setSearchText] = React.useState("");

  const { funcOpts, colOpts } = React.useMemo(() => {
    const funcOpts = formulaFunctions
      .filter((f) => matchesTypes(f, possibleTypes))
      .map((f) => ({
        value: f.id,
        label: f.id,
        description: f.description,
        type: "function",
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
    const colOpts = columns
      .filter((c) => matchesTypes(c, possibleTypes))
      .map((c) => ({
        value: c.key,
        label: asKey(c.col),
        type: "column",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { funcOpts, colOpts };
  }, [possibleTypes, columns]);

  const opts = React.useMemo(() => {
    let allOpts = [
      {
        label: "Functions",
        options: funcOpts.map((opt) => ({
          ...opt,
          label: (
            <div style={{ padding: "4px 0", maxWidth: "100%" }}>
              <div
                style={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {opt.value}
              </div>
              {opt.description && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-tertiary)",
                    marginTop: 2,
                    whiteSpace: "normal",
                    lineHeight: "1.2",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {opt.description}
                </div>
              )}
            </div>
          ),
          value: opt.value,
          data: { ...opt },
        })),
      },
      {
        label: "Columns",
        options: colOpts.map((opt) => ({
          ...opt,
          label: (
            <div style={{ padding: "4px 0", maxWidth: "100%" }}>
              <div
                style={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {opt.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--color-text-secondary)",
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {opt.type}
              </div>
            </div>
          ),
          value: opt.value,
          data: { ...opt },
        })),
      },
    ];

    // Add numeric constant option if search text is a valid number
    const num = Number(searchText);
    if (!Number.isNaN(num)) {
      allOpts.unshift({
        label: "Constant",
        options: [
          {
            value: searchText,
            label: (
              <div style={{ padding: "4px 0", maxWidth: "100%" }}>
                <div
                  style={{
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {searchText}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-secondary)",
                    marginTop: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  number
                </div>
              </div>
            ),
            type: "constant",
            data: {
              type: "constant",
              value: searchText,
              label: searchText,
            },
          },
        ],
      });
    }

    return allOpts;
  }, [funcOpts, colOpts, searchText]);

  const selectedFunc = funcOpts.find((f) => f.value === colOrFunc);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        width: "100%",
        minWidth: "300px",
        marginRight: 8,
      }}
    >
      <Select<string>
        value={colOrFunc || null}
        showSearch
        options={opts}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
        }}
        optionFilterProp="value"
        listHeight={300}
        dropdownStyle={{
          padding: "4px 0",
          minWidth: 200,
          maxWidth: "90vw",
          overflowX: "hidden",
        }}
        dropdownMatchSelectWidth={false}
        popupMatchSelectWidth={false}
        onSearch={setSearchText}
        onChange={(val) => {
          setColOrfunc(val || undefined);
          const isFunc = funcOpts.some((f) => f.value === val);
          update(isFunc ? "function" : "column", val as string);
        }}
        filterOption={(input: string, option: any) => {
          if (!option?.value) return false;

          // For numeric input, only match constant type options
          if (!Number.isNaN(Number(input))) {
            return option.data?.type === "constant";
          }

          // Otherwise use default filtering
          return String(option.value)
            .toLowerCase()
            .includes(input.toLowerCase());
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {selectedFunc?.description && (
          <CustomTooltip content={selectedFunc.description}>
            <InfoCircleOutlined
              style={{ color: token.colorTextDescription, flexShrink: 0 }}
            />
          </CustomTooltip>
        )}
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            setColOrfunc(undefined);
            setSearchText("");
            update("column", "");
          }}
          style={{ padding: "0 4px", flexShrink: 0 }}
        />
      </div>
    </div>
  );
}

const funcLabel = (id: string) => id;

type OptionType = {
  value: string;
  label: string;
  description?: string;
  type: string;
  data?: {
    value: string;
    label: string;
    description?: string;
    type: string;
  };
};

type GroupedOptionType = {
  label: string;
  options: OptionType[];
};

type SelectOptionType = OptionType | GroupedOptionType;

type FormulaInputProps = {
  formula?: Formula;
  columns: ColInfo[];
  updateFormula: (kind: "formula" | "column", value: Formula | string) => void;
  possibleTypes: undefined | string[];
  placeholder?: string;
  root: boolean;
  level?: number;
};

function FormulaInput({
  formula,
  columns,
  updateFormula,
  possibleTypes,
  placeholder,
  root = true,
  level = 0,
}: FormulaInputProps) {
  const { result: valid, message } = isValid(formula, columns);
  const { token } = theme.useToken();

  const { funcOpts, colOpts } = React.useMemo(() => {
    const funcOpts = formulaFunctions
      .filter((f) => matchesTypes(f, possibleTypes))
      .map((f) => ({
        value: f.id,
        label: f.id,
        description: f.description,
        type: "function",
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
    const colOpts = columns
      .filter((c) => matchesTypes(c, possibleTypes))
      .map((c) => ({
        value: c.key,
        label: asKey(c.col),
        type: "column",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { funcOpts, colOpts };
  }, [possibleTypes, columns]);

  const [func, setFunc] = React.useState<string | undefined>(
    Array.isArray(formula) ? (formula[0] as string) : undefined
  );
  const initialParams = (formula?.slice(1) || []) as (string | Formula)[];
  const [params, setParams] = React.useState(initialParams);

  const handleFormulaChange = (
    newFunc: string | undefined,
    newParams: (string | Formula)[]
  ) => {
    if (newFunc) {
      updateFormula("formula", [newFunc, ...newParams]);
    } else {
      updateFormula("formula", "");
    }
  };

  const opts = React.useMemo(() => {
    const funcGroup = {
      label: "Functions",
      options: funcOpts.map((opt) => ({
        ...opt,
        label: (
          <div
            style={{
              padding: "4px 0",
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.value}
            </div>
            {opt.description && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--color-text-tertiary)",
                  marginTop: 2,
                  whiteSpace: "normal",
                  lineHeight: "1.2",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {opt.description}
              </div>
            )}
          </div>
        ),
        value: opt.value,
      })),
    };

    const colGroup = {
      label: "Columns",
      options: colOpts.map((opt) => ({
        ...opt,
        label: (
          <div
            style={{
              padding: "4px 0",
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.label}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.type}
            </div>
          </div>
        ),
        value: opt.value,
      })),
    };

    return [funcGroup, colGroup];
  }, [funcOpts, colOpts]);

  const singleOptions = React.useMemo(
    () =>
      funcOpts.map((opt) => ({
        ...opt,
        label: (
          <div style={{ padding: "4px 0" }}>
            <div style={{ fontWeight: 500 }}>{opt.value}</div>
            {opt.description && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--color-text-tertiary)",
                  marginTop: 2,
                }}
              >
                {opt.description}
              </div>
            )}
          </div>
        ),
        value: opt.value,
      })),
    [funcOpts]
  );

  const { paramLengths, paramTypeOptions } = figureFormulaParams(
    func,
    params,
    columns
  );

  if (params.length < paramLengths) {
    const missing = new Array(paramLengths - params.length).fill("");
    setParams([...params, ...missing]);
  } else if (params.length > paramLengths) {
    setParams(params.slice(0, paramLengths));
  }

  React.useEffect(() => {
    if (formula) {
      setFunc(Array.isArray(formula) ? (formula[0] as string) : undefined);
    }
  }, [formula]);

  const selectedFunc = funcOpts.find((f) => f.value === func);

  return (
    <div
      style={{
        marginBottom: 12,
        marginLeft: level > 0 ? 8 : 0,
        marginTop: 4,
        paddingLeft: level > 0 ? 8 : 0,
        borderLeft: level > 0 ? `2px solid ${token.colorBorder}` : "none",
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minWidth: 0,
        }}
      >
        {level > 0 && (
          <div
            style={{
              color: token.colorTextTertiary,
              fontSize: 12,
              backgroundColor: token.colorBgTextHover,
              padding: "2px 4px",
              borderRadius: 4,
              whiteSpace: "nowrap",
            }}
          >
            Step {level}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            padding: "6px 8px 6px 4px",
            backgroundColor: token.colorBgContainer,
            borderRadius: 4,
            border: "none",
            width: "100%",
            minWidth: 0,
            marginRight: 8,
          }}
        >
          <Select<string>
            value={func || null}
            showSearch={!root}
            options={root ? singleOptions : opts}
            placeholder={placeholder || "Select function or column"}
            style={{
              flex: 1,
              minWidth: 0,
            }}
            optionFilterProp="value"
            listHeight={300}
            dropdownStyle={{
              padding: "4px 0",
              minWidth: 200,
              maxWidth: "90vw",
              overflowX: "hidden",
            }}
            dropdownMatchSelectWidth={false}
            popupMatchSelectWidth={false}
            onChange={(val) => {
              setFunc(val || undefined);
              const isFunc = funcOpts.some((f) => f.value === val);
              if (isFunc) {
                handleFormulaChange(val as string, params);
              } else {
                updateFormula("column", val as string);
              }
            }}
          />
          {selectedFunc?.description && (
            <CustomTooltip content={selectedFunc.description}>
              <InfoCircleOutlined
                style={{ color: token.colorTextDescription, flexShrink: 0 }}
              />
            </CustomTooltip>
          )}
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setFunc(undefined);
              setParams([]);
              handleFormulaChange(undefined, []);
            }}
            style={{ padding: "0 4px", flexShrink: 0 }}
          />
        </div>
      </div>

      {params.length > 0 && (
        <div
          style={{
            padding: "4px 8px 4px 4px",
            backgroundColor: "#fafafa",
            borderRadius: 6,
            width: "100%",
          }}
        >
          {params.map((param, idx) =>
            Array.isArray(param) ? (
              <div key={idx} style={{ position: "relative", width: "100%" }}>
                <FormulaInput
                  root={false}
                  formula={param}
                  columns={columns}
                  placeholder={`Parameter ${idx + 1}`}
                  possibleTypes={paramTypeOptions?.map((p) => p[idx])}
                  level={level + 1}
                  updateFormula={(_kind, value) => {
                    const newParams = [
                      ...params.slice(0, idx),
                      value,
                      ...params.slice(idx + 1),
                    ];
                    setParams(newParams);
                    handleFormulaChange(func as string, newParams);
                  }}
                />
              </div>
            ) : (
              <div key={idx}>
                <ColumnInput
                  key={idx}
                  column={param}
                  columns={columns}
                  placeholder={`Enter parameter ${idx + 1}`}
                  possibleTypes={paramTypeOptions?.map((p) => p[idx])}
                  update={(kind, value) => {
                    const newItem =
                      kind === "function" ? ([value] as Formula) : value;
                    const newParams = [
                      ...params.slice(0, idx),
                      newItem,
                      ...params.slice(idx + 1),
                    ];
                    setParams(newParams);
                    handleFormulaChange(func as string, newParams);
                  }}
                />
              </div>
            )
          )}
        </div>
      )}

      {!valid && message && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 12px",
            backgroundColor: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 4,
            color: "#cf1322",
            fontSize: 13,
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}
